import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from './firebase'

/**
 * Get user's IP address (best effort)
 */
async function getUserIP(): Promise<string | null> {
  try {
    // Try multiple IP services for reliability
    const services = [
      'https://api.ipify.org?format=json',
      'https://ipapi.co/json/',
      'https://httpbin.org/ip'
    ]
    
    for (const service of services) {
      try {
        const response = await fetch(service, { timeout: 3000 } as any)
        const data = await response.json()
        
        // Different services return IP in different formats
        const ip = data.ip || data.origin || data.query
        if (ip && typeof ip === 'string') {
          return ip
        }
      } catch (err) {
        continue // Try next service
      }
    }
    
    return null
  } catch (error) {
    console.warn('Could not get IP address:', error)
    return null
  }
}

/**
 * Log authentication attempt with IP and user agent
 */
export async function logAuthAttempt(
  type: 'signup' | 'login' | 'failed_login',
  email: string,
  userId?: string,
  error?: string
) {
  try {
    const ip = await getUserIP()
    const userAgent = navigator.userAgent
    const timestamp = new Date()
    
    await addDoc(collection(db, 'security_logs'), {
      type,
      email,
      userId: userId || null,
      ip,
      userAgent,
      error: error || null,
      timestamp: serverTimestamp(),
      localTimestamp: timestamp.toISOString(),
      // Additional context
      referrer: document.referrer || null,
      url: window.location.href,
      // Browser fingerprinting (basic)
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })
    
    console.log(`🔒 Security log: ${type} attempt for ${email} from IP ${ip}`)
  } catch (error) {
    console.error('Failed to log auth attempt:', error)
    // Don't throw - logging failure shouldn't break auth flow
  }
}

/**
 * Log suspicious activity
 */
export async function logSuspiciousActivity(
  activity: string,
  details: Record<string, any> = {},
  userId?: string
) {
  try {
    const ip = await getUserIP()
    
    await addDoc(collection(db, 'security_logs'), {
      type: 'suspicious_activity',
      activity,
      details,
      userId: userId || null,
      ip,
      userAgent: navigator.userAgent,
      timestamp: serverTimestamp(),
      localTimestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer || null
    })
    
    console.warn(`🚨 Suspicious activity logged: ${activity} from IP ${ip}`)
  } catch (error) {
    console.error('Failed to log suspicious activity:', error)
  }
}

/**
 * Log file upload attempts (for monitoring abuse)
 */
export async function logFileUpload(
  fileName: string,
  fileSize: number,
  fileType: string,
  folder: string,
  userId: string,
  success: boolean,
  error?: string
) {
  try {
    const ip = await getUserIP()
    
    await addDoc(collection(db, 'security_logs'), {
      type: 'file_upload',
      fileName,
      fileSize,
      fileType,
      folder,
      userId,
      success,
      error: error || null,
      ip,
      timestamp: serverTimestamp(),
      localTimestamp: new Date().toISOString()
    })
  } catch (error) {
    console.error('Failed to log file upload:', error)
  }
}
